import type { TargetEnv } from '#types/env'
import type { OptimizelyCmabBlob, OptimizelyCmabMap, OptimizelyDecideCallOptions } from '#types/p13n'

/**
 * CMS `experienceType` value that requires Optimizely `decideAsync`.
 * @category Utils
 */
export const OPTIMIZELY_CMS_CONTEXTUAL_CMAB = 'contextual_multi_armed_bandit'

/**
 * How long to wait for CMAB user attributes before calling `decideAsync`.
 * @category Utils
 */
export const OPTIMIZELY_CMAB_ATTRIBUTE_WAIT_TIMEOUT_MS = 3000

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Picks the Optimizely CMAB blob key for the current target environment.
 * Production reads `cmab_prod`; every other env reads `cmab_nonprod`.
 * @param targetEnv - Runtime `targetEnv` (`DEV` / `QA` / `PREPROD` / `PROD`).
 * @returns Blob key inside the `optimizely` store.
 * @category Utils
 */
export const getOptimizelyCmabBlobKey = (targetEnv?: TargetEnv | string) =>
  targetEnv === 'PROD' ? 'cmab_prod' : 'cmab_nonprod'

/**
 * Returns whether a value is the Optimizely CMAB blob wrapper.
 * An empty object is valid (fail-open). A non-empty object must use the
 * `url-matches` / `cmab-static-experiments` keys — not the legacy flat map.
 * @param value - Unknown payload from Netlify Blobs or `vfaConfig`.
 * @returns `true` when the value can be read as an `OptimizelyCmabBlob`.
 * @category Utils
 */
export const isOptimizelyCmabBlob = (value: unknown): value is OptimizelyCmabBlob => {
  if (!isPlainObject(value)) return false

  const urlMatches = value['url-matches']
  const experiments = value['cmab-static-experiments']

  if (urlMatches != null && !isPlainObject(urlMatches)) return false
  if (experiments != null && !Array.isArray(experiments)) return false

  if (urlMatches == null && experiments == null && Object.keys(value).length > 0) return false

  return true
}

/**
 * Reads the URL-key → attributes map from a CMAB blob.
 * @param blob - Blob wrapper from Netlify Blobs / `vfaConfig.optimizelyCmabMap`.
 * @returns URL-matches map, or `undefined` when the blob is missing.
 * @category Utils
 */
export const getOptimizelyCmabUrlMatches = (
  blob: OptimizelyCmabBlob | null | undefined,
): OptimizelyCmabMap | undefined => {
  if (!isOptimizelyCmabBlob(blob)) return undefined

  const urlMatches = blob['url-matches']
  return isPlainObject(urlMatches) ? urlMatches : undefined
}

/**
 * Reads the non-CMS CMAB flag keys from a CMAB blob.
 * @param blob - Blob wrapper from Netlify Blobs / `vfaConfig.optimizelyCmabMap`.
 * @returns Flag keys that must use `decideAsync`. Empty when the blob is missing.
 * @category Utils
 */
export const getOptimizelyCmabStaticExperiments = (
  blob: OptimizelyCmabBlob | null | undefined,
): string[] => {
  if (!isOptimizelyCmabBlob(blob) || !Array.isArray(blob['cmab-static-experiments'])) return []

  return blob['cmab-static-experiments'].filter((key): key is string => typeof key === 'string' && key !== '')
}

/**
 * Returns whether a non-CMS flag should use CMAB `decideAsync`.
 * @param flagKey - Optimizely flag key.
 * @param blob - Blob wrapper from Netlify Blobs / `vfaConfig.optimizelyCmabMap`.
 * @returns `true` when the flag is listed in `cmab-static-experiments`.
 * @category Utils
 */
export const isOptimizelyCmabStaticExperiment = (
  flagKey: string,
  blob: OptimizelyCmabBlob | null | undefined,
): boolean => !!flagKey && getOptimizelyCmabStaticExperiments(blob).includes(flagKey)

/**
 * Normalizes CMS `attribute_keys` into a list of non-empty strings.
 * @param attributeKeys - CMS `attribute_keys` payload.
 * @returns Attribute names to wait for before `decideAsync`.
 * @category Utils
 */
export const normalizeOptimizelyAttributeKeys = (attributeKeys: unknown): string[] => {
  if (!Array.isArray(attributeKeys)) return []

  return attributeKeys.filter((key): key is string => typeof key === 'string' && key !== '')
}

/**
 * Builds `decide` options for a CMS Optimizely experiment.
 * Only `contextual_multi_armed_bandit` uses `decideAsync` and waits on `attribute_keys`.
 * @param experienceType - CMS `experienceType`.
 * @param attributeKeys - CMS `attribute_keys`.
 * @returns Decide options, or an empty object for non-CMAB experience types.
 * @category Utils
 */
export const getOptimizelyCmsCmabDecideOptions = (
  experienceType?: string,
  attributeKeys?: unknown,
): OptimizelyDecideCallOptions => {
  if (experienceType !== OPTIMIZELY_CMS_CONTEXTUAL_CMAB) return {}

  return {
    async: true,
    waitForAttributeKeys: normalizeOptimizelyAttributeKeys(attributeKeys),
  }
}

/**
 * Builds Optimizely attributes from a CMAB URL-key map for the landing path.
 * Checks longer URL keys first; the most specific matching key wins per attribute.
 * Non-matching attributes are omitted.
 * @param path - Landing pathname (not the current route or full URL).
 * @param map - URL-key → `{ attributeName: value }` map from the CMAB blob `url-matches`.
 * @returns Flat attribute map to merge into Optimizely user attributes.
 * @category Utils
 */
export const getOptimizelyCmabAttributes = (
  path: string | undefined,
  map: OptimizelyCmabMap | null | undefined,
): Record<string, string> => {
  if (!path || !isPlainObject(map)) return {}

  const attributes: Record<string, string> = {}

  for (const urlKey of Object.keys(map).sort((a, b) => b.length - a.length)) {
    if (!urlKey || !path.includes(urlKey)) continue

    const values = map[urlKey]
    if (!isPlainObject(values)) continue

    for (const [name, value] of Object.entries(values)) {
      if (!name || attributes[name] !== undefined) continue
      if (value === undefined || value === null || value === '') continue
      attributes[name] = String(value)
    }
  }

  return attributes
}
